import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiPatch } from '../../lib/api';
import { FacilityType, StaffRole } from '@bisa/shared';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';

interface Facility {
  id: string;
  name: string;
  slug: string;
  address: string;
  phone: string;
  facilityType: string;
  lat?: number;
  lng?: number;
  isActive: boolean;
}

interface FacilityForm {
  name: string;
  slug: string;
  address: string;
  phone: string;
  facilityType: FacilityType;
  lat: string;
  lng: string;
}

interface StaffMember {
  id: string;
  userId: string;
  email?: string | null;
  role: StaffRole;
  isActive: boolean;
  createdAt: string;
}

const emptyFacilityForm: FacilityForm = {
  name: '',
  slug: '',
  address: '',
  phone: '',
  facilityType: FacilityType.CLINIC,
  lat: '',
  lng: '',
};

const roleLabels: Record<StaffRole, string> = {
  [StaffRole.ADMIN]: 'Admin — Facility Manager',
  [StaffRole.FRONT_DESK]: 'Front Desk / Reception',
  [StaffRole.PROVIDER]: 'Provider / Consultant',
};

const roleColors: Record<StaffRole, string> = {
  [StaffRole.ADMIN]: 'text-green-700 bg-green-50',
  [StaffRole.FRONT_DESK]: 'text-blue-700 bg-blue-50',
  [StaffRole.PROVIDER]: 'text-purple-700 bg-purple-50',
};

// ─── Staff management sub-panel ─────────────────────────────────────────────

function FacilityStaffModal({ facility, onClose }: { facility: Facility; onClose: () => void }) {
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', role: StaffRole.ADMIN, tempPassword: '' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addedTempPw, setAddedTempPw] = useState<string | null>(null);
  const [addedEmail, setAddedEmail] = useState<string | null>(null);

  const [resetLinkFor, setResetLinkFor] = useState<StaffMember | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetLinkLoading, setResetLinkLoading] = useState(false);
  const [resetLinkError, setResetLinkError] = useState<string | null>(null);
  const [resetLinkCopied, setResetLinkCopied] = useState(false);

  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);
  const [editForm, setEditForm] = useState({ role: StaffRole.ADMIN, isActive: true });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<StaffMember[]>(`/admin/facilities/${facility.id}/staff`);
      setMembers(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [facility.id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddSaving(true);
    setAddError(null);
    try {
      await apiPost(`/admin/facilities/${facility.id}/staff`, addForm);
      setAddedTempPw(addForm.tempPassword);
      setAddedEmail(addForm.email);
      setAddForm({ email: '', role: StaffRole.ADMIN, tempPassword: '' });
      await load();
    } catch (e) {
      setAddError(String(e));
    } finally {
      setAddSaving(false);
    }
  };

  const openResetLink = async (m: StaffMember) => {
    setResetLinkFor(m);
    setResetLink(null);
    setResetLinkError(null);
    setResetLinkCopied(false);
    setResetLinkLoading(true);
    try {
      const { link } = await apiPost<{ link: string }>(`/admin/facilities/${facility.id}/staff/${m.id}/reset-link`);
      setResetLink(link);
    } catch (e) {
      setResetLinkError(String(e));
    } finally {
      setResetLinkLoading(false);
    }
  };

  const openEdit = (m: StaffMember) => {
    setEditingMember(m);
    setEditForm({ role: m.role, isActive: m.isActive });
    setEditError(null);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMember) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await apiPatch(`/admin/facilities/${facility.id}/staff/${editingMember.id}`, editForm);
      setMembers((prev) =>
        prev.map((m) => m.id === editingMember.id ? { ...m, ...editForm } : m),
      );
      setEditingMember(null);
    } catch (e) {
      setEditError(String(e));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <Modal
      open
      size="lg"
      title={`Staff — ${facility.name}`}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div className="space-y-4">
        {/* Add user form toggle */}
        {!showAdd && !editingMember && !resetLinkFor && (
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { setShowAdd(true); setAddedTempPw(null); }}>+ Add User</Button>
          </div>
        )}

        {/* Add user form */}
        {showAdd && (
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
            {addedTempPw ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-800">User created — share these credentials:</p>
                <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm space-y-1">
                  <p><span className="font-medium">Email:</span> {addedEmail}</p>
                  <p><span className="font-medium">Temp password:</span> <span className="font-mono">{addedTempPw}</span></p>
                </div>
                <p className="text-xs text-gray-500">Give these to the user. They should change their password after first login.</p>
                <Button size="sm" onClick={() => { setShowAdd(false); setAddedTempPw(null); }}>Done</Button>
              </div>
            ) : (
              <form onSubmit={handleAdd} className="space-y-3">
                <p className="text-sm font-medium text-blue-900">Add new user</p>
                <Input
                  label="Email"
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="user@facility.com"
                  required
                />
                <Select
                  label="Role"
                  value={addForm.role}
                  onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value as StaffRole }))}
                >
                  {Object.values(StaffRole).map((r) => (
                    <option key={r} value={r}>{roleLabels[r]}</option>
                  ))}
                </Select>
                <Input
                  label="Temporary Password (min 8 chars)"
                  type="text"
                  value={addForm.tempPassword}
                  onChange={(e) => setAddForm((f) => ({ ...f, tempPassword: e.target.value }))}
                  placeholder="e.g. Clinic@2024"
                  required
                  minLength={8}
                />
                {addError && <p className="text-sm text-red-600">{addError}</p>}
                <div className="flex gap-2">
                  <Button type="submit" size="sm" loading={addSaving}>Create User</Button>
                  <Button size="sm" variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Reset link panel */}
        {resetLinkFor && (
          <div className="rounded-lg border border-orange-100 bg-orange-50 p-4">
            <p className="text-sm font-medium text-orange-900 mb-2">
              Password Reset Link — {resetLinkFor.email}
            </p>
            {resetLinkLoading && <p className="text-sm text-gray-500">Generating link…</p>}
            {resetLinkError && <p className="text-sm text-red-600">{resetLinkError}</p>}
            {resetLink && (
              <div className="space-y-2">
                <p className="text-xs text-gray-600">Copy this link and send it to the user via WhatsApp or SMS. It expires after one use.</p>
                <div className="flex gap-2 items-center">
                  <input
                    readOnly
                    value={resetLink}
                    className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-mono text-gray-700"
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { navigator.clipboard.writeText(resetLink); setResetLinkCopied(true); }}
                  >
                    {resetLinkCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
            <Button size="sm" variant="ghost" className="mt-3" onClick={() => setResetLinkFor(null)}>Close</Button>
          </div>
        )}

        {/* Edit form */}
        {editingMember && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Edit — {editingMember.email ?? editingMember.userId}
            </p>
            <form onSubmit={handleEdit} className="space-y-3">
              <Select
                label="Role"
                value={editForm.role}
                onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value as StaffRole }))}
              >
                {Object.values(StaffRole).map((r) => (
                  <option key={r} value={r}>{roleLabels[r]}</option>
                ))}
              </Select>
              <Select
                label="Status"
                value={editForm.isActive ? 'true' : 'false'}
                onChange={(e) => setEditForm((f) => ({ ...f, isActive: e.target.value === 'true' }))}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
              {editError && <p className="text-sm text-red-600">{editError}</p>}
              <div className="flex gap-2">
                <Button type="submit" size="sm" loading={editSaving}>Save</Button>
                <Button size="sm" variant="secondary" onClick={() => setEditingMember(null)}>Cancel</Button>
              </div>
            </form>
          </div>
        )}

        {/* Staff table */}
        {loading && <PageSpinner />}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && !error && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Email / User', 'Role', 'Status', 'Since', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-400">
                    No users yet. Add the first one above.
                  </td></tr>
                )}
                {members.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{m.email ?? m.userId}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${roleColors[m.role]}`}>
                        {roleLabels[m.role] ?? m.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${m.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {new Date(m.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="secondary" onClick={() => openEdit(m)}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => openResetLink(m)}>Reset PW</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Kiosk setup modal ───────────────────────────────────────────────────────

interface KioskConfig {
  facilityId: string;
  facilitySlug: string;
  facilityName: string;
  apiBaseUrl: string;
  kioskPin: string;
}

function KioskSetupModal({ facility, onClose }: { facility: Facility; onClose: () => void }) {
  const [config, setConfig] = useState<KioskConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pinVisible, setPinVisible] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSaved, setPinSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    apiGet<KioskConfig>(`/admin/facilities/${facility.id}/kiosk-config`)
      .then(setConfig)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [facility.id]);

  const handlePinSave = async () => {
    if (!/^\d{4}$/.test(newPin)) { setPinError('PIN must be exactly 4 digits'); return; }
    setPinSaving(true); setPinError(null);
    try {
      await apiPost(`/admin/facilities/${facility.id}/kiosk-pin`, { pin: newPin });
      setConfig((c) => c ? { ...c, kioskPin: newPin } : c);
      setNewPin('');
      setPinSaved(true);
      setTimeout(() => setPinSaved(false), 3000);
    } catch (e) {
      setPinError(String(e));
    } finally {
      setPinSaving(false);
    }
  };

  const buildSetupLink = (cfg: KioskConfig) => {
    const base = 'https://kiosk.bisa.tiwaak.com/provision';
    const p = new URLSearchParams({
      fid: cfg.facilityId,
      slug: cfg.facilitySlug,
      name: cfg.facilityName,
      pin: cfg.kioskPin,
      api: cfg.apiBaseUrl,
    });
    return `${base}?${p.toString()}`;
  };

  const copySetupLink = () => {
    if (!config) return;
    navigator.clipboard.writeText(buildSetupLink(config));
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const copySheet = () => {
    if (!config) return;
    const link = buildSetupLink(config);
    const sheet = [
      '── BISA Kiosk Setup ──',
      `Facility: ${config.facilityName}`,
      '',
      'Option 1 — One-tap setup (recommended):',
      `Open this link on the tablet browser:`,
      link,
      '',
      'Option 2 — Manual setup:',
      `API URL:     ${config.apiBaseUrl}`,
      `Facility ID: ${config.facilityId}`,
      `Slug:        ${config.facilitySlug}`,
      `Kiosk PIN:   ${config.kioskPin}`,
      '  1. Open kiosk.bisa.tiwaak.com on the tablet',
      '  2. Tap the facility name 5 times',
      '  3. Enter the PIN above',
    ].join('\n');
    navigator.clipboard.writeText(sheet);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const copyField = (val: string) => navigator.clipboard.writeText(val);

  return (
    <Modal open size="lg" title={`Kiosk Setup — ${facility.name}`} onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      {loading && <PageSpinner />}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {config && (
        <div className="space-y-5">
          {/* Setup info */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 divide-y divide-gray-200">
            {[
              { label: 'API URL', value: config.apiBaseUrl },
              { label: 'Facility ID (UUID)', value: config.facilityId, mono: true },
              { label: 'Slug', value: config.facilitySlug, mono: true },
              { label: 'Display Name', value: config.facilityName },
            ].map(({ label, value, mono }) => (
              <div key={label} className="flex items-center justify-between px-4 py-3 gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
                  <p className={`text-sm text-gray-900 truncate ${mono ? 'font-mono' : ''}`}>{value}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copyField(value)}>Copy</Button>
              </div>
            ))}
            {/* PIN row */}
            <div className="flex items-center justify-between px-4 py-3 gap-3">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Kiosk PIN</p>
                <p className="text-sm font-mono text-gray-900">{pinVisible ? config.kioskPin : '••••'}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setPinVisible((v) => !v)}>
                  {pinVisible ? 'Hide' : 'Show'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => copyField(config.kioskPin)}>Copy</Button>
              </div>
            </div>
          </div>

          {/* Primary: one-tap setup link */}
          <div className="rounded-lg border border-brand/30 bg-blue-50 p-4">
            <p className="text-sm font-semibold text-blue-900 mb-1">One-tap setup link</p>
            <p className="text-xs text-blue-700 mb-3">
              Send this link to the clinic — opening it on the tablet configures the kiosk automatically.
            </p>
            <Button onClick={copySetupLink} className="w-full">
              {linkCopied ? '✓ Link copied!' : 'Copy Setup Link'}
            </Button>
          </div>

          {/* Copy full sheet (includes link + manual fallback) */}
          <Button variant="secondary" onClick={copySheet} className="w-full">
            {copied ? '✓ Copied to clipboard' : 'Copy Full Setup Sheet (WhatsApp / SMS)'}
          </Button>

          {/* Reset PIN */}
          <div className="rounded-lg border border-orange-100 bg-orange-50 p-4">
            <p className="text-sm font-semibold text-orange-900 mb-3">Reset Kiosk PIN</p>
            <div className="flex gap-2 items-start">
              <input
                type="text"
                maxLength={4}
                placeholder="New 4-digit PIN"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                className="w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              <Button onClick={handlePinSave} loading={pinSaving} disabled={newPin.length !== 4}>
                {pinSaved ? '✓ Saved' : 'Set PIN'}
              </Button>
            </div>
            {pinError && <p className="mt-2 text-sm text-red-600">{pinError}</p>}
            <p className="mt-2 text-xs text-orange-700">
              After resetting, send the new PIN to the clinic. The kiosk will reject the old PIN on next online check.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function FacilitiesPage() {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Facility | null>(null);
  const [form, setForm] = useState<FacilityForm>(emptyFacilityForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [staffFacility, setStaffFacility] = useState<Facility | null>(null);
  const [kioskFacility, setKioskFacility] = useState<Facility | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet<Facility[]>('/admin/facilities');
      setFacilities(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyFacilityForm);
    setShowModal(true);
  };

  const openEdit = (f: Facility) => {
    setEditing(f);
    setForm({
      name: f.name,
      slug: f.slug,
      address: f.address,
      phone: f.phone,
      facilityType: f.facilityType as FacilityType,
      lat: f.lat != null ? String(f.lat) : '',
      lng: f.lng != null ? String(f.lng) : '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    const body = {
      name: form.name,
      address: form.address,
      phone: form.phone,
      facilityType: form.facilityType,
      lat: form.lat ? Number(form.lat) : undefined,
      lng: form.lng ? Number(form.lng) : undefined,
    };
    try {
      if (editing) {
        await apiPut(`/admin/facilities/${editing.id}`, body);
      } else {
        await apiPost('/admin/facilities', { ...body, slug: form.slug });
      }
      setShowModal(false);
      await load();
    } catch (e) {
      setSaveError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (f: Facility) => {
    await apiPut(`/admin/facilities/${f.id}`, { isActive: !f.isActive });
    setFacilities((prev) => prev.map((x) => x.id === f.id ? { ...x, isActive: !x.isActive } : x));
  };

  const set = (k: keyof FacilityForm, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Facilities</h1>
        <Button onClick={openCreate}>+ Add Facility</Button>
      </div>

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Slug', 'Type', 'Address', 'Phone', 'Active', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {facilities.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    No facilities yet. Add your first one.
                  </td>
                </tr>
              )}
              {facilities.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{f.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono">{f.slug}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{f.facilityType}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${f.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                      {f.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => openEdit(f)}>Edit</Button>
                      <Button size="sm" onClick={() => setStaffFacility(f)}>Users</Button>
                      <Button size="sm" variant="secondary" onClick={() => setKioskFacility(f)}>Kiosk</Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(f)}>
                        {f.isActive ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Facility create/edit modal */}
      <Modal
        open={showModal}
        title={editing ? 'Edit Facility' : 'Add Facility'}
        onClose={() => { setShowModal(false); setSaveError(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button form="facility-form" type="submit" loading={saving}>{editing ? 'Save' : 'Create'}</Button>
          </>
        }
      >
        <form id="facility-form" onSubmit={handleSubmit} className="space-y-4">
          <Input label="Facility Name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
          <Input
            label="Slug (URL-safe identifier)"
            value={form.slug}
            onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
            placeholder="e.g. korle-bu-teaching"
            required={!editing}
            disabled={!!editing}
          />
          <Select label="Facility Type" value={form.facilityType} onChange={(e) => set('facilityType', e.target.value)}>
            {Object.values(FacilityType).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
          <Input label="Address" value={form.address} onChange={(e) => set('address', e.target.value)} required />
          <Input label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+233XXXXXXXXX" required />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Latitude (optional)"
              type="number"
              step="any"
              value={form.lat}
              onChange={(e) => set('lat', e.target.value)}
              placeholder="5.6037"
            />
            <Input
              label="Longitude (optional)"
              type="number"
              step="any"
              value={form.lng}
              onChange={(e) => set('lng', e.target.value)}
              placeholder="-0.1870"
            />
          </div>
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </form>
      </Modal>

      {staffFacility && (
        <FacilityStaffModal facility={staffFacility} onClose={() => setStaffFacility(null)} />
      )}
      {kioskFacility && (
        <KioskSetupModal facility={kioskFacility} onClose={() => setKioskFacility(null)} />
      )}
    </div>
  );
}
